import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Modal,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "components/Table";
import ServiceFeeModal from "components/Modals/ServiceFee";
import ConfirmationModal from "components/Modals/Confirmation";
import { deleteClientCost, getClientCosts } from "api/resources/clientCosts";
import { ClientCost } from "api/models/ClientCost";
import {
  SERVICE_FEE_TYPE_LABELS,
  isServiceFeeType,
  serviceFeeDescription,
} from "helpers/serviceFees";
import {
  PERIOD_STATUS_ORDER,
  periodStatus,
  periodStatusChips,
} from "helpers/periodStatus";

const DISPLAY_DATE_FORMAT = "DD.MM.YYYY";

const byStatusThenNewest = (a: ClientCost, b: ClientCost) =>
  PERIOD_STATUS_ORDER.indexOf(periodStatus(a)) -
    PERIOD_STATUS_ORDER.indexOf(periodStatus(b)) ||
  dayjs(b.effectiveFrom).diff(dayjs(a.effectiveFrom));

type ServiceFeesProps = {
  clientId: number;
  clientContractType?: string;
};

const ServiceFees = ({ clientId, clientContractType }: ServiceFeesProps) => {
  const [serviceFees, setServiceFees] = useState<ClientCost[] | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [editedServiceFee, setEditedServiceFee] = useState<ClientCost | null>(
    null,
  );
  const [serviceFeeToDelete, setServiceFeeToDelete] =
    useState<ClientCost | null>(null);
  const [showPastFees, setShowPastFees] = useState(false);

  const pastFeeCount = (serviceFees ?? []).filter(
    (serviceFee) => periodStatus(serviceFee) === "PAST",
  ).length;
  const visibleServiceFees = (serviceFees ?? [])
    .filter((serviceFee) => showPastFees || periodStatus(serviceFee) !== "PAST")
    .sort(byStatusThenNewest);

  const loadServiceFees = useCallback(() => {
    getClientCosts(clientId)
      .then((costs) =>
        setServiceFees(costs.filter((cost) => isServiceFeeType(cost.costType))),
      )
      .catch((err) => {
        console.error("Failed to load service fees:", err);
        setServiceFees([]);
      });
  }, [clientId]);

  useEffect(() => {
    loadServiceFees();
  }, [loadServiceFees]);

  const handleDeleteServiceFee = (id: number) => {
    deleteClientCost(clientId, id)
      .then(() => {
        setServiceFeeToDelete(null);
        loadServiceFees();
      })
      .catch((err) => {
        console.error("Failed to delete service fee:", err);
      });
  };

  const columns = [
    {
      accessorKey: "effectiveFrom",
      header: "Valid from",
      cell: (row: any) =>
        dayjs(row.row.original.effectiveFrom).format(DISPLAY_DATE_FORMAT),
    },
    {
      accessorKey: "effectiveTo",
      header: "Valid to",
      cell: (row: any) =>
        dayjs(row.row.original.effectiveTo).format(DISPLAY_DATE_FORMAT),
    },
    {
      accessorKey: "costType",
      header: "Fee type",
      cell: (row: any) => {
        const { costType } = row.row.original as ClientCost;

        return isServiceFeeType(costType)
          ? SERVICE_FEE_TYPE_LABELS[costType]
          : costType;
      },
    },
    {
      accessorKey: "value",
      header: "Service fee",
      cell: (row: any) => serviceFeeDescription(row.row.original),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        const chip = periodStatusChips[periodStatus(row.row.original)];

        return <Chip size="small" label={chip.label} color={chip.color} />;
      },
    },
    {
      accessorKey: "actions",
      header: "",
      cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            aria-label="Edit service fee"
            onClick={() => setEditedServiceFee(row.row.original)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Delete service fee"
            onClick={() => setServiceFeeToDelete(row.row.original)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showPastFees}
              onChange={(event) => setShowPastFees(event.target.checked)}
            />
          }
          label={`Show past fees (${pastFeeCount})`}
          disabled={pastFeeCount === 0}
          sx={{ mr: "1.5rem" }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddModal(true)}
        >
          Add service fee
        </Button>
      </Box>
      <Table
        data={visibleServiceFees}
        columns={columns}
        isFetching={!serviceFees}
        pageCount={1}
        noDataText={
          pastFeeCount > 0
            ? "No active or upcoming service fees"
            : "No service fees have been set"
        }
      />
      <Modal open={addModal} onClose={() => setAddModal(false)}>
        <ServiceFeeModal
          clientId={clientId}
          clientContractType={clientContractType}
          onSaved={loadServiceFees}
          onClose={() => setAddModal(false)}
        />
      </Modal>
      <Modal
        open={editedServiceFee !== null}
        onClose={() => setEditedServiceFee(null)}
      >
        <ServiceFeeModal
          clientId={clientId}
          clientContractType={clientContractType}
          serviceFee={editedServiceFee ?? undefined}
          onSaved={loadServiceFees}
          onClose={() => setEditedServiceFee(null)}
        />
      </Modal>
      <Modal
        open={serviceFeeToDelete !== null}
        onClose={() => setServiceFeeToDelete(null)}
      >
        <ConfirmationModal
          title="Do you want to delete this service fee period?"
          subtitle="Reports for this period will no longer include this service fee."
          buttonTitle="Delete"
          action={() => handleDeleteServiceFee(serviceFeeToDelete.id)}
          setConfirmationModal={() => setServiceFeeToDelete(null)}
        />
      </Modal>
    </>
  );
};

export default ServiceFees;
